<template>
  <div class="photo-uploader" v-bind="$attrs">
    <label
      class="photo-uploader__label"
      :class="{
        'photo-uploader__label-enter': entering,
        'photo-uploader__with-img': file || image,
      }"
      :aria-label="hint"
      @dragenter.prevent="entering = true"
      @dragleave.prevent="entering = false"
      @drop.prevent="handleDrop"
    >
      <input type="file" @input="handleInput" accept=".jpg, .png" />

      <span
        class="photo-uploader__badge"
        v-if="primary && (file || image)"
      >{{ $t("settings.primary_photo") }}</span>

      <button
        type="button"
        class="photo-uploader__delete"
        v-if="file || image"
        :aria-label="$t('settings.delete_photo')"
        @click.stop.prevent="onDeleteClick"
      >
        <VIcon icon="mdi-trash-can-outline" size="14" />
      </button>

      <span class="photo-uploader__placeholder" v-if="!file && !image">
        <img :src="PhotoPlaceholder" alt="" />
        <span class="photo-uploader__hint" v-if="hint">{{ hint }}</span>
      </span>

      <span class="photo-uploader__preview" v-if="file">
        <span
          class="photo-uploader__preview-img"
          :style="{
            backgroundImage: `url(${prepareSrc(file)})`,
          }"
        ></span>
        <span class="photo-uploader__overlay">{{ $t("change") }}</span>
      </span>

      <span class="photo-uploader__preview" v-if="image && !file">
        <span
          class="photo-uploader__preview-img"
          :style="{
            backgroundImage: `url(${image!.url})`,
          }"
        ></span>
        <span class="photo-uploader__overlay">{{ $t("change") }}</span>
      </span>
    </label>
  </div>
</template>

<script lang="ts" setup>
import PhotoPlaceholder from "@/main/assets/img/upload.svg";
import { onUnmounted, watch } from "vue";
import { onMounted } from "vue";
import { ref } from "vue";

const events = ["dragenter", "dragleave", "dragover", "drag"];
const allowedExtensions = ["jpg", "png", "jpeg"];

onMounted(() => {
  events.forEach((event) =>
    document.body.addEventListener(event, (e) => e.preventDefault())
  );
});

onUnmounted(() => {
  events.forEach((event) =>
    document.body.removeEventListener(event, (e) => e.preventDefault())
  );
});

const file = ref<File>();

const props = defineProps<{
  image?: { id: number; url: string };
  primary?: boolean;
  hint?: string;
}>();

watch(
  props,
  (newValue, oldValue) => {
    if (newValue.image?.id) {
      file.value = undefined;
    }
  },
  {
    deep: true,
  }
);
const emit = defineEmits<{
  (
    e: "updateImages",
    value: {
      id?: number;
      url: File;
    }
  ): void;
  (e: "deleteImage", value: { id: number }): void;
  (e: "removePendingUpload", value: { url: File; id?: number }): void;
}>();

const entering = ref(false);

const handleDrop = (e: DragEvent) => {
  entering.value = false;
  let fileList = e.dataTransfer?.files as FileList;
  const filteredFiles = [...fileList].filter((value) =>
    allowedExtensions.includes(value.name.split(".").pop()!)
  );
  if (filteredFiles.length) {
    file.value = filteredFiles[0];

    emit("updateImages", {
      id: props.image?.id,
      url: file.value,
    });
  }
};

const handleInput = (e: Event) => {
  const fileList = (e.target as HTMLInputElement).files as FileList;
  const filteredFiles = [...fileList].filter((value) =>
    allowedExtensions.includes(value.name.split(".").pop()!)
  );
  if (filteredFiles.length) {
    file.value = filteredFiles[0];

    emit("updateImages", {
      id: props.image?.id,
      url: file.value,
    });
  }
};

const prepareSrc = (file: File) => URL.createObjectURL(file);

const onDeleteClick = () => {
  if (file.value) {
    emit("removePendingUpload", { url: file.value, id: props.image?.id });
    file.value = undefined;
    return;
  }
  if (props.image?.id) {
    emit("deleteImage", { id: props.image.id });
  }
};
</script>

<style lang="scss" scoped>
.photo-uploader {
  &__label {
    position: relative;
    width: 100%;
    height: 100%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    border-radius: 10px;
    background: #e7eaee;
    border: 1px dashed #c2c7cf;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    &:hover {
      border-color: #403838;
    }

    &-enter {
      background: #b3b6b9;
      border-color: #403838;
    }
  }

  input {
    visibility: hidden;
    display: none;
  }

  &__placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 8px;
    text-align: center;
  }

  &__hint {
    color: #696e7c;
    font-size: 12px;
    line-height: 16px;
    max-width: 160px;
  }

  &__badge {
    position: absolute;
    top: 8px;
    left: 8px;
    z-index: 1;
    background: rgba(64, 56, 56, 0.85);
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    padding: 3px 8px;
    border-radius: 999px;
  }

  &__delete {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border: none;
    border-radius: 50%;
    background: rgba(64, 56, 56, 0.85);
    color: #fff;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background: #c62828;
    }
  }

  &__with-img {
    background: transparent;
    border-style: solid;
    border-color: transparent;
  }

  &__preview {
    position: relative;
    display: block;
    width: 100%;
    height: 100%;

    &-img {
      display: block;
      width: 100%;
      height: 100%;
      background-position: center;
      background-size: cover;
      border-radius: 10px;
    }
  }

  &__overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    background: rgba(0, 0, 0, 0.45);
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    opacity: 0;
    transition: opacity 0.15s ease;
    pointer-events: none;
  }

  &__label:hover &__overlay {
    opacity: 1;
  }
}
</style>
